/**
 * API-health vocabulary (#325) — the SINGLE source of every operator-facing string
 * the honest-degradation surface adds: the full-width outage banner + the
 * AnomalyQueue "unreachable" copy + the rail "Alerts" unknown-count glyph.
 * Centralized so `apiHealthVocabulary.lock.test.ts` can reflectively prove none of it
 * drifts into a forbidden group — the same guard the Ontario-IMS / feed / default-view
 * locks use.
 *
 * Honesty invariant (the whole point of #325): every string here asserts
 * UNREACHABILITY ONLY — "cannot confirm" — and NEVER a fabricated count nor a false
 * all-clear. The banner leads with ⚠ and closes with the verbatim disclaimer
 * "This is not an all-clear." A total /api outage otherwise renders as a clean quiet
 * board (no alerts, badge "0") — a dangerous FALSE all-clear this vocabulary exists to
 * prevent.
 */

/** The verbatim closing disclaimer. Kept as its own export so the lock can (a) assert
 *  the banner carries it and (b) strip it before scanning for an AFFIRMATIVE
 *  all-clear — the banner's honesty rests on this very negation, so it must be exempt
 *  from the affirmative-all-clear guard. */
export const NOT_AN_ALL_CLEAR = 'This is not an all-clear.';

/** Humanize an elapsed span into a compact age label (m / h / d). Mirrors
 *  FeedDownChip.humanizeFeedDownAge so the two honest-chrome surfaces read alike. */
export function humanizeApiDownAge(ageMs: number): string {
  const mins = Math.max(0, Math.floor(ageMs / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * The full-width outage banner copy. `lastReachedLabel` is the local HH:MM of the last
 * confirmed reach (null if the API was never confirmed this session); `ageMs` is how
 * long ago that was. Asserts unreachability ONLY — never a count, never an all-clear.
 */
export function apiUnreachableBanner(
  lastReachedLabel: string | null,
  ageMs: number | null,
): string {
  const confirmed =
    lastReachedLabel !== null && ageMs !== null
      ? `last confirmed ${lastReachedLabel} (${humanizeApiDownAge(ageMs)} ago)`
      : 'not confirmed this session';
  return `⚠ Cannot reach live data — ${confirmed}. Alerts and counts below may be stale or missing. ${NOT_AN_ALL_CLEAR}`;
}

/** AnomalyQueue empty-state while the API is unreachable — replaces the false
 *  "No urgent alerts in view" all-clear (distinguishing UNKNOWN from ZERO is the fix). */
export const ALERTS_UNAVAILABLE_EMPTY = 'Alerts unavailable — cannot reach live data';

/** The rail "Alerts" badge glyph when the count is UNKNOWN (unreachable) — an em
 *  dash, NEVER "0". */
export const ALERTS_UNKNOWN_BADGE = '—';

/** Tooltip/title for the rail "Alerts" badge while the count is unknown. */
export const ALERTS_UNKNOWN_TITLE = 'Urgent Alerts · unavailable — cannot reach live data';
